import cm from 'classnames';

import { FinalCut, RenderStatus } from 'services/finalcut.service';
import { useAppSelector, useAppDispatch } from 'store/store-hooks';
import { IntroKeys } from 'components/app/intro/intro-keys';
import { updateEntity, setRemovingInstance, selectHelpMode } from 'components/app/_mediator/app-data.slice';
import { setSelectedFinalcut } from 'components/app/projects/_mediator/projects.slice';
import { updFcName } from 'components/app/finalcuts/_mediator/fc-data.slice';

import { ReactComponent as TrashIcon } from 'assets/ui/trash.svg';

import { Tooltip } from 'components/UI/tooltip/tooltip';
import { EditCell } from 'components/app/app-components/edit-cell/edit-cell';
import { FcStatus } from 'components/app/app-components/fc-status/fc-status';

import { FcStallProgressMessage } from './fc-stall-progress-message';

import styles from './_finalcuts-list.module.scss';

interface Props {
  element: FinalCut;
  progress?: number;
  positionInQueue?: number;
}

export const FinalcutsListElement = ({ element, progress, positionInQueue }: Props) => {
  const { errorMessageForUser, id, name, updatedAt, createdAt } = element;
  const { finalcuts, removingInstance } = useAppSelector((state) => state.app__data);
  const { selectedFinalcut } = useAppSelector((state) => state.projects);
  const helpMode = useAppSelector(selectHelpMode);
  const stallProgress = useAppSelector((state) => state.fc__renderServer.fcStallProgress[id]);
  const dispatch = useAppDispatch();

  const onSelect = () => {
    dispatch(setSelectedFinalcut(id));
    dispatch(setRemovingInstance({ instance: 'finalcut', id: id, modalOpen: false }));
  };

  const onRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(setRemovingInstance({ instance: 'finalcut', id: id, modalOpen: true }));
  };

  const updateName = async (id: number, name: string) => {
    const res = await dispatch(updateEntity({ id: id, name: name, entity: 'finalcuts' })).unwrap();
    if (id === selectedFinalcut && res.url) dispatch(updFcName({ name, url: res.url }));
  };

  return (
    <Tooltip
      dynamicPosition
      showTooltip={helpMode}
      tip={
        element.status === RenderStatus.pending && !progress ? (
          <div>
            The render server is starting up — this may take 2-3 minutes and then rendering will begin.
            <br />
            You'll receive an email when rendering is complete, so you can step away.
          </div>
        ) : undefined
      }
    >
      <div
        key={id}
        onClick={onSelect}
        data-intro={IntroKeys.fcElem}
        className={cm(styles.FcList__element, {
          [styles['FcList__element--selected']]: selectedFinalcut === id,
          [styles['FcList__element--removing']]:
            removingInstance?.instance === 'finalcut' && removingInstance.modalOpen && removingInstance?.id === id,
        })}
      >
        <TrashIcon className={styles.FcList__removeBtn} onClick={onRemove} />
        {errorMessageForUser && <div className={styles.FcList__error}>{errorMessageForUser}</div>}
        <EditCell
          element={{ name, id }}
          selectedID={selectedFinalcut}
          onSubmit={(element) => updateName(element.id, element.name)}
          selectedName={finalcuts?.find((finalcut) => finalcut.id === selectedFinalcut)?.name ?? ''}
          wrapperTag="div"
          width={90}
        />
        <h4>{updatedAt ?? createdAt}</h4>
        <p className={cm(styles.FcList__status)}>
          Status: <FcStatus fc={element} />
        </p>
        {!!positionInQueue && <p>Queue Position: {positionInQueue}</p>}
        {typeof progress === 'number' && <p>{progress}%</p>}
        <FcStallProgressMessage stallProgress={stallProgress} />
      </div>
    </Tooltip>
  );
};
